import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Shield, X, Plus } from "lucide-react";
import { Card, CardHeader, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { useI18n } from "i18n";
import api from "lib/api";

const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "pt", label: "Português" },
  { value: "es", label: "Español" },
  { value: "it", label: "Italiano" },
];

function Toggle({ checked, onChange, label, description }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-border p-4">
      <div className="space-y-0.5">
        <Label>{label}</Label>
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          checked ? "bg-emerald-500" : "bg-muted"
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? "translate-x-6" : "translate-x-1"
          }`}
        />
      </button>
    </div>
  );
}

export default function CompanyForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const isEditing = !!id;

  const [activeTab, setActiveTab] = useState("dados");
  const [name, setName] = useState("");
  const [language, setLanguage] = useState("en");
  const [country, setCountry] = useState("");
  const [contestantCanSetLanguage, setContestantCanSetLanguage] = useState(true);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditing);
  const [error, setError] = useState("");

  // Password policy state
  const [policy, setPolicy] = useState({
    min_length: 8,
    require_uppercase: true,
    require_lowercase: true,
    require_special_char: true,
    blacklist_words: "",
  });
  const [policyLoading, setPolicyLoading] = useState(false);
  const [policySaving, setPolicySaving] = useState(false);
  const [policyMsg, setPolicyMsg] = useState("");
  const [newWord, setNewWord] = useState("");

  useEffect(() => {
    if (isEditing) {
      Promise.all([
        api.get(`/api/companies/${id}/`),
        api.get(`/api/companies/${id}/password-policy/`),
      ])
        .then(([companyRes, policyRes]) => {
          setName(companyRes.data.name);
          setLanguage(companyRes.data.language || "en");
          setCountry(companyRes.data.country || "");
          setContestantCanSetLanguage(companyRes.data.contestant_can_set_language !== false);
          setPolicy(policyRes.data);
        })
        .catch(() => {
          setError(t("error.loadCompany"));
        })
        .finally(() => setFetching(false));
    }
  }, [id, isEditing]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const data = {
        name,
        language,
        country: country.toUpperCase(),
        contestant_can_set_language: contestantCanSetLanguage,
      };
      if (isEditing) {
        await api.put(`/api/companies/${id}/`, data);
      } else {
        await api.post("/api/companies/", data);
      }
      navigate("/liveness/admin/companies");
    } catch (err) {
      const detail = err.response?.data;
      if (typeof detail === "object") {
        const messages = Object.values(detail).flat().join(" ");
        setError(messages);
      } else {
        setError(t("error.saveCompany"));
      }
    } finally {
      setLoading(false);
    }
  };

  const getBlacklistWords = () => {
    const words = policy.blacklist_words || "";
    return words
      .split(",")
      .map((w) => w.trim())
      .filter((w) => w);
  };

  const addWord = () => {
    if (!newWord.trim()) return;
    const current = getBlacklistWords();
    const word = newWord.trim().toLowerCase();
    if (current.includes(word)) {
      setNewWord("");
      return;
    }
    setPolicy({
      ...policy,
      blacklist_words: [...current, word].join(","),
    });
    setNewWord("");
  };

  const removeWord = (wordToRemove) => {
    const current = getBlacklistWords();
    setPolicy({
      ...policy,
      blacklist_words: current.filter((w) => w !== wordToRemove).join(","),
    });
  };

  const handleSavePolicy = async () => {
    setPolicySaving(true);
    setPolicyMsg("");
    try {
      const res = await api.put(`/api/companies/${id}/password-policy/`, {
        min_length: policy.min_length,
        require_uppercase: policy.require_uppercase,
        require_lowercase: policy.require_lowercase,
        require_special_char: policy.require_special_char,
        blacklist_words: policy.blacklist_words,
      });
      setPolicy(res.data);
      setPolicyMsg(t("policy.saved"));
      setTimeout(() => setPolicyMsg(""), 3000);
    } catch {
      setPolicyMsg(t("error.savePolicy"));
    } finally {
      setPolicySaving(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  }

  const tabs = [{ id: "dados", label: t("company.tab.data") }];
  if (isEditing) {
    tabs.push({ id: "password_policy", label: t("company.tab.policy") });
  }

  return (
    <div className="animate-fade-in">
      <button
        onClick={() => navigate("/liveness/admin/companies")}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("common.back")}
      </button>

      <h1 className="text-2xl font-bold tracking-tight mb-1">
        {isEditing ? t("company.edit") : t("company.new")}
      </h1>
      <p className="text-sm text-muted-foreground mb-6">
        {isEditing ? t("company.editSubtitle") : t("company.newSubtitle")}
      </p>

      {/* Tabs */}
      {tabs.length > 1 && (
        <div className="flex gap-1 mb-6 border-b border-border">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab.id
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Tab: Dados */}
      {activeTab === "dados" && (
        <Card className="w-full">
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="company-name">{t("common.name")}</Label>
                <Input
                  id="company-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("company.namePlaceholder")}
                  required
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company-language">{t("common.language")}</Label>
                <select
                  id="company-language"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  required
                >
                  {LANGUAGES.map((lang) => (
                    <option key={lang.value} value={lang.value}>
                      {lang.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="company-country">{t("common.country")}</Label>
                <Input
                  id="company-country"
                  value={country}
                  onChange={(e) =>
                    setCountry(
                      e.target.value.replace(/[^a-zA-Z]/g, "").slice(0, 2).toUpperCase()
                    )
                  }
                  placeholder={t("common.country.placeholder")}
                  maxLength={2}
                />
              </div>
              <Toggle
                checked={contestantCanSetLanguage}
                onChange={setContestantCanSetLanguage}
                label={t("company.canSetLang")}
                description={t("company.canSetLang.desc")}
              />
              {error && <p className="text-sm text-red-500">{error}</p>}
              <div className="flex justify-end gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/liveness/admin/companies")}
                >
                  {t("common.cancel")}
                </Button>
                <Button type="submit" loading={loading}>
                  {isEditing ? t("common.save") : t("common.create")}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Tab: Política de Senha */}
      {activeTab === "password_policy" && isEditing && (
        <div className="space-y-6">
          {policyMsg && (
            <div
              className={`rounded-lg p-3 text-sm ${
                policyMsg === t("policy.saved")
                  ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-400"
                  : "bg-red-500/10 border border-red-500/30 text-red-400"
              }`}
            >
              {policyMsg}
            </div>
          )}

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-emerald-500" />
                <div>
                  <h2 className="text-lg font-semibold">{t("policy.title")}</h2>
                  <p className="text-sm text-muted-foreground">
                    Configuração específica para esta empresa. Tem precedência
                    sobre a política do sistema.
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="policy-min-length">{t("policy.minLength")}</Label>
                  <Input
                    id="policy-min-length"
                    type="number"
                    min="8"
                    max="40"
                    value={policy.min_length}
                    onChange={(e) =>
                      setPolicy({
                        ...policy,
                        min_length: parseInt(e.target.value) || 8,
                      })
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    Número mínimo de caracteres (8-40).
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <Toggle
                  checked={policy.require_uppercase}
                  onChange={(v) =>
                    setPolicy({ ...policy, require_uppercase: v })
                  }
                  label={t("policy.requireUpper")}
                  description="A senha deve conter ao menos uma letra maiúscula (A-Z)."
                />
                <Toggle
                  checked={policy.require_lowercase}
                  onChange={(v) =>
                    setPolicy({ ...policy, require_lowercase: v })
                  }
                  label={t("policy.requireLower")}
                  description="A senha deve conter ao menos uma letra minúscula (a-z)."
                />
                <Toggle
                  checked={policy.require_special_char}
                  onChange={(v) =>
                    setPolicy({ ...policy, require_special_char: v })
                  }
                  label={t("policy.requireSpecial")}
                  description="A senha deve conter ao menos um caractere especial (!@#$%...)."
                />
              </div>

              {/* Blacklist Words */}
              <div className="space-y-3">
                <Label>{t("policy.blacklist")}</Label>
                <p className="text-xs text-muted-foreground">
                  Palavras que não podem estar contidas na senha.
                </p>
                <div className="flex gap-2">
                  <Input
                    placeholder={t("policy.blacklistPlaceholder")}
                    value={newWord}
                    onChange={(e) => setNewWord(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addWord()}
                    className="max-w-xs"
                  />
                  <Button variant="outline" onClick={addWord}>
                    <Plus className="w-4 h-4 mr-2" />
                    {t("common.add")}
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {getBlacklistWords().map((word) => (
                    <span
                      key={word}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-muted text-muted-foreground border border-border"
                    >
                      {word}
                      <button
                        onClick={() => removeWord(word)}
                        className="hover:bg-destructive/20 rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  {getBlacklistWords().length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      Nenhuma palavra bloqueada.
                    </p>
                  )}
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  onClick={handleSavePolicy}
                  loading={policySaving}
                  disabled={policyLoading}
                >
                  {t("common.save")}
                </Button>
              </div>

              {/* Info */}
              <div className="rounded-lg bg-blue-500/10 border border-blue-500/30 p-4 mt-4">
                <h4 className="text-sm font-medium text-blue-400 mb-2">
                  Sobre a Política da Empresa
                </h4>
                <ul className="text-xs text-blue-300/80 space-y-1">
                  <li>
                    - Esta política se aplica apenas a usuários vinculados a esta
                    empresa.
                  </li>
                  <li>
                    - Tem precedência sobre a política geral do sistema.
                  </li>
                  <li>
                    - Se não configurada, a política do sistema será utilizada.
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
